/**
 * The hosted checkout, opened over whatever screen asked for it.
 *
 * paygate's loader is the entire integration: it makes a full-viewport iframe,
 * hands it the client secret in the URL *fragment* — fragments are not sent to
 * servers, so a secret never lands in an access log or a Referer — and posts
 * back what happened. The panel renders no card field of its own, which is the
 * point: a card number that touches ondit's DOM puts ondit in PCI scope.
 *
 * The origin is not passed to the loader. It derives one from its own script
 * tag and checks every `postMessage` against it, so a trailing slash in the
 * environment variable would silently fail that check and leave an invisible
 * iframe over the page.
 */

const ORIGIN = (import.meta.env.VITE_PAYGATE_ORIGIN || "").replace(/\/+$/, "");

interface Checkout {
	openCheckout(config: { clientSecret: string; onSuccess?: (paymentId: string) => void; onClose?: () => void }): {
		close(): void;
	};
}

declare global {
	interface Window {
		Paygate?: () => Checkout;
	}
}

/** Set when this deployment can take a payment at all. */
export const canCheckout = ORIGIN !== "";

let loading: Promise<() => Checkout> | null = null;

function loader(): Promise<() => Checkout> {
	const ready = window.Paygate;
	if (ready) return Promise.resolve(ready);
	if (!canCheckout)
		return Promise.reject(new Error("VITE_PAYGATE_ORIGIN is not set: this build cannot take a payment"));
	// One script tag for the life of the tab, however many buttons race for it.
	loading ??= new Promise((resolve, reject) => {
		const el = document.createElement("script");
		el.src = `${ORIGIN}/loader.js`;
		el.async = true;
		el.onload = () => {
			const fn = window.Paygate;
			if (fn) resolve(fn);
			else reject(new Error("the checkout loaded but defined nothing"));
		};
		el.onerror = () => {
			// Cleared so a customer who lost their connection can click again.
			loading = null;
			el.remove();
			reject(new Error(`could not reach the checkout at ${ORIGIN}`));
		};
		document.head.appendChild(el);
	});
	return loading;
}

/**
 * Opens the checkout and settles when the customer is done with it: the payment
 * id if they paid, null if they closed it.
 *
 * Closing is not an error. It is the ordinary way out of a price somebody
 * decided against, and a screen that reports it as a failure teaches them that
 * the panel is broken.
 */
export async function openCheckout(clientSecret: string): Promise<string | null> {
	const Paygate = await loader();
	return new Promise((resolve) => {
		Paygate().openCheckout({
			clientSecret,
			onSuccess: (paymentId) => resolve(paymentId),
			onClose: () => resolve(null),
		});
	});
}

/** The gateway callback can precede our signed webhook. Never infer access from it. */
export async function waitForSubscription(check: () => Promise<boolean>): Promise<boolean> {
	for (let attempt = 0; attempt < 10; attempt++) {
		if (await check()) return true;
		if (attempt < 9) await new Promise((resolve) => setTimeout(resolve, 1500));
	}
	return false;
}
